import { TriangleAlert } from 'lucide-react';
import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AI_USE_POLICY_URL, PRIVACY_OFFICE_EMAIL } from '@/components/legal-warning';

/** The weight the redesign draws its lucide marks at. */
const ICON_STROKE_WIDTH = 1.75;

/**
 * The brand dark (#627A56) rather than the brand green: on this panel's
 * yellow-50 ground #7A996B reaches 3.08:1 and fails AA for 12 and 13px text,
 * where #627A56 reaches 4.58:1 and passes.
 *
 * Underlined as well as coloured, so the links do not depend on a 4.58:1
 * difference to be found.
 */
const LINK = 'text-[var(--ds-color-text-brand-dark)] underline';

/** Ties the panel's landmark to the heading that names it. */
const HEADING_ID = 'legal-warning-heading';

/**
 * The document-upload legal warning, as the 280px panel beside the create
 * project form. Figma 11914:189875.
 *
 * ## Why the copy is longer than the frame's
 *
 * The frame draws the intro and a single bullet holding all four categories in
 * one run-on sentence, with the AI Use Policy footer and the Privacy Office
 * link dropped. That is the designer's paste of the warning, not a rewrite of
 * it: the four categories and both links are what AECOM Legal signed off, they
 * are what `components/legal-warning.tsx` puts in front of every upload, and a
 * shorter warning on the screen that creates the project would be the one place
 * in the product that says less. So the geometry is the frame's and the words
 * are the policy's, unchanged.
 *
 * ## Why it is not the design system's Callout
 *
 * `Callout` is one strip: icon, label and body on a line, 16 of padding, and
 * `warning` grounds it in amber-50. This panel stacks a heading row over an
 * intro over a list, at 24 of padding on yellow-50. Composed for the same
 * reason `add-context-modal.tsx` composes rather than taking the DS Dialog.
 *
 * ## Why it reads the same keys as the legacy block
 *
 * One set of words for one warning. `__tests__/legal-warning-panel.test.tsx`
 * pins that this panel renders every `legal-warning.*` key the legacy block
 * does, so a category added to one cannot quietly go missing from the other.
 */
export const LegalWarningPanel: FC = () => {
  const { t } = useTranslation();

  return (
    /* Named from its own heading. The page has a second complementary
       landmark — the brand rail — and two unnamed ones are indistinguishable
       in a landmark list. */
    <aside
      aria-labelledby={HEADING_ID}
      className="flex w-full flex-col gap-3 rounded-[var(--ds-radius-10)] bg-[var(--ds-color-raw-yellow-50)] p-6 lg:w-[280px] lg:shrink-0"
    >
      <div className="flex items-center gap-2">
        {/* Decorative: the heading beside it is what names the panel. */}
        <TriangleAlert
          aria-hidden="true"
          className="size-4 shrink-0"
          strokeWidth={ICON_STROKE_WIDTH}
        />
        {/* h2 rather than a styled p — the form's own h1 is the page heading. */}
        <h2
          className="min-w-0 flex-1 text-sm font-medium leading-5 text-[var(--ds-color-text-primary)]"
          id={HEADING_ID}
        >
          {t('legal-warning.heading')}
        </h2>
      </div>

      <div className="flex flex-col gap-2 text-[var(--ds-color-text-primary)]">
        <p className="text-xs leading-4">{t('legal-warning.intro')}</p>

        <ul className="ml-5 list-outside list-disc space-y-1 text-[13px] leading-5">
          <li>
            <Trans
              components={{
                privacy: (
                  <a className={LINK} href={`mailto:${PRIVACY_OFFICE_EMAIL}`}>
                    {t('legal-warning.privacy-link')}
                  </a>
                ),
              }}
              i18nKey="legal-warning.pii"
            />
          </li>
          <li>{t('legal-warning.classification')}</li>
          <li>{t('legal-warning.data-sovereignty')}</li>
          <li>{t('legal-warning.ai-prohibited')}</li>
        </ul>

        <p className="text-xs leading-4">
          <Trans
            components={{
              policy: (
                <a
                  className={LINK}
                  href={AI_USE_POLICY_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('legal-warning.policy-link')}
                </a>
              ),
            }}
            i18nKey="legal-warning.footer"
          />
        </p>
      </div>
    </aside>
  );
};
